import React, { useCallback, useEffect } from 'react';
import { RoutedComponentProps } from 'src/app-routes';
import { generatePath, useParams } from 'react-router';
import { ILexiconTagsPaginationAPI } from 'src/services';
import { useAppSelector } from 'src/hooks';
import { LexiconRuleModal } from 'src/components-bl/Lexicon/components/LexiconRules/LexiconRuleModal';
import { LexiconTagType } from 'src/services/src/service/lexicon/types';
import { useDispatch } from 'react-redux';
import { useValidateLocale } from '../../hooks';
import { editLexiconRuleContainerActions } from './EditLexiconRuleContainer.actions';

export type EditLexiconRuleContainerProps = RoutedComponentProps & {
  refetchLexiconTags?: () => Promise<ILexiconTagsPaginationAPI>;
};

export const EditLexiconRuleContainer = ({
  permittedRouteMap,
  refetchLexiconTags,
}: EditLexiconRuleContainerProps): JSX.Element | null => {
  const dispatch = useDispatch();
  const shopId = useAppSelector(state => state.shop.current?.shopId);
  const currentLexiconRule = useAppSelector(state => state.lexicon.currentLexiconRule);

  const { id: lexiconRuleId } = useParams<{ id: string }>();

  const locale = useValidateLocale({ permittedRouteMap, dispatch, shopId })?.locale;

  const onModalClose = useCallback(() => {
    if (permittedRouteMap.lexiconCategories?.path && shopId && locale) {
      dispatch(
        editLexiconRuleContainerActions.navigateTo({
          navigateTo: generatePath(permittedRouteMap.lexiconCategories.path, {
            shopId,
            locale,
            tagType: LexiconTagType.RenameRules,
          }),
        })
      );
    }
  }, [dispatch, permittedRouteMap.lexiconCategories, locale, shopId]);

  const onSuccess = useCallback(() => {
    refetchLexiconTags?.();
  }, [refetchLexiconTags]);

  useEffect(() => {
    if (!lexiconRuleId) {
      onModalClose();
      return;
    }

    if (shopId && locale && currentLexiconRule?.id !== lexiconRuleId) {
      (
        dispatch(
          editLexiconRuleContainerActions.getLexiconRule({
            shopId,
            locale,
            id: lexiconRuleId,
          })
        ) as any
      )
        .unwrap()
        .catch((error: unknown) => {
          console.error(error);
          onModalClose();
        });
    }
  }, [lexiconRuleId, shopId, locale]);

  useEffect(() => {
    return () => {
      dispatch(editLexiconRuleContainerActions.resetLexiconRule());
    };
  }, []);

  if (!shopId || !locale) {
    return null;
  }

  return shopId ? (
    <LexiconRuleModal
      dispatch={dispatch}
      mode='edit'
      onClose={onModalClose}
      onSuccess={onSuccess}
      shopId={shopId}
      locale={locale}
      lexiconRule={currentLexiconRule}
    />
  ) : null;
};
